import React, { useEffect, useMemo } from 'react';
import { StyledOptions, StyledDeliveryAddressSummary } from './styled';
import { useTranslation } from '~/shared/utils/translation';
import { DeliveryAddressSummary } from '../DeliveryAddressSummery/DeliveryAddressSummary';
import {
    StyledCheckoutAside,
    StyledCheckoutMain,
    StyledCheckoutMobileSubmit,
    StyledCheckoutWrapper,
} from '~/templates/pages/components/CheckOutLayout/styled';
import { useCheckout } from '../../hooks/useCheckout';
import useProducts from '~/features/commerce-api/hooks/useProducts';
import { SplitDeliverySummary } from '../SplitDeliverySummary/SplitDeliverySummary';
import { DeliverySummary } from '../DeliverySummary';
import { useSubmit } from '../../hooks/useSubmit';
import { DeliveryAddressOptionProps } from '../../models/fields';
import { withErrorBoundary } from '~/shared/utils/errorBoundary';
import { useBasket } from '~/features/basket/hooks';
import { useIsValid } from './lib/useIsValid';
import { useCheckoutUtils } from './lib/useCheckoutUtils';
import { useShippingAddress } from './lib/useShippingAddress';
import useInitDelivery from '../../hooks/useInitDelivery';
import { ShippingMethods } from './ShippingMethods';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useDeliveryEstimate } from './lib/useDeliveryEstimate';
import { DeliveryEstimateDialog } from './components/DeliveryEstimateDialog/DeliveryEstimateDialog';
import { CheckoutSummary } from '../CheckoutSummary/CheckoutSummary';

export const DeliveryAddressOption = withErrorBoundary(
    ({
        numberOfShipments,
        currentShipmentIndex,
        onSubmit,
        shipmentId,
        showPreviousShipment,
    }: DeliveryAddressOptionProps) => {
        const { translate } = useTranslation();
        const basket = useBasket();
        const customer = useCustomer();
        const { handlers: submitHandlers } = useSubmit();
        const { products, getProductsByShipment } = useProducts();
        const {
            currentShipmentStep,
            setDeliveryDate,
            deliveryDate,
            shipmentType,
            isAlternateShippingValid,
            setShippingAddressInformation,
            carrier,
            carriers,
            deliveryMethod,
            alternateShippingAddress,
            vendorSelectedDroppoint,
        } = useCheckout();

        const {
            hasNonStandardDelivery,
            longestDeliveryEstimate,
            isDeliveryDialogOpen,
            setIsDeliveryDialogOpen,
        } = useDeliveryEstimate(shipmentId);

        const isValid = useIsValid();
        const { getAddress } = useShippingAddress();
        const { getCtaLabel, getDays } = useCheckoutUtils();
        const { isTruckDelivery, showShippingMethods } = useInitDelivery();

        // Get delivery days from first available carrier when no selection is made
        const getFirstAvailableDeliveryDays = () => {
            if (!carriers) return null;

            // Priority order: clickAndCollect -> dropPoint -> addressDelivery -> truckDelivery -> virtual
            const priorityOrder = [
                carriers.clickAndCollect,
                carriers.dropPoint,
                carriers.addressDelivery,
                carriers.truckDelivery,
                carriers.virtual,
            ];

            for (const carrierGroup of priorityOrder) {
                if (carrierGroup && carrierGroup.length > 0) {
                    const carrierWithDeliveryTime = carrierGroup.find(
                        (carrier) => carrier.c_estimatedArrivalTime,
                    );
                    if (carrierWithDeliveryTime) {
                        return carrierWithDeliveryTime.c_estimatedArrivalTime;
                    }
                }
            }

            return null;
        };

        /**
         * function that checks if
         * delivery info is set
         * */
        const handleStep = async () => {
            try {
                if (!isValid) return;

                // Check if we need to show delivery estimate warning (only for store personnel)
                if (customer?.isStorePersonnel && hasNonStandardDelivery()) {
                    console.log('Opening delivery estimate dialog for store personnel');
                    setIsDeliveryDialogOpen(true);
                    return;
                }

                console.log('Otherwise proceeding with delivery step');
                await processDeliveryStep();
            } catch (e) {
                console.error(e);
            }
        };

        // Function to handle the confirmed dialog
        const handleDeliveryDialogConfirm = async () => {
            try {
                await processDeliveryStep();
            } catch (e) {
                console.error(e);
            }
        };

        // When user denies in the dialog
        const handleDeliveryDialogCancel = () => {
            // Stay on the Delivery page
            setIsDeliveryDialogOpen(false);
        };

        // Common functionality for processing the delivery step
        const processDeliveryStep = async () => {
            const shippingAddress = getAddress(
                isAlternateShippingValid,
                alternateShippingAddress,
                deliveryMethod,
            );

            if (shippingAddress && deliveryMethod) {
                await submitHandlers.submitShippingAddress(shippingAddress, shipmentId);

                if (carrier && 'id' in carrier[deliveryMethod]) {
                    await basket.setShippingMethod(
                        carrier[deliveryMethod].id,
                        shipmentId,
                        shippingAddress?.storeId as unknown as string | undefined,
                        deliveryDate,
                    );
                }
            }

            onSubmit();
        };

        useEffect(() => {
            if (isTruckDelivery) {
                setShippingAddressInformation();

                if (basket.c_suggestedStore) {
                    basket.setSuggestedStore(null).catch((error) => {
                        console.warn('Could not clear suggested store', error);
                    });
                }
            }
        }, [isTruckDelivery, alternateShippingAddress, basket.c_suggestedStore]);

        const productCount = useMemo(() => {
            return {
                items: String(getProductsByShipment(shipmentId)?.length),
                total: String(products?.length),
            };
        }, [getProductsByShipment, products, shipmentId]);

        return (
            <StyledCheckoutWrapper>
                <StyledCheckoutMain>
                    <StyledOptions>
                        {!isTruckDelivery && numberOfShipments === 1 && (
                            <DeliverySummary
                                title={translate('checkout.delivery.title')}
                                subtitle={
                                    deliveryMethod === 'virtual'
                                        ? ''
                                        : translate('checkout.delivery.subtitle')
                                }
                                showSummaryList={deliveryMethod === 'virtual'}
                                products={getProductsByShipment(shipmentId)}
                            />
                        )}

                        {!isTruckDelivery && numberOfShipments > 1 && (
                            <SplitDeliverySummary
                                products={getProductsByShipment(shipmentId)}
                                title={translate('checkout.splitdelivery.title', {
                                    current: String(currentShipmentIndex),
                                    total: String(numberOfShipments),
                                })}
                                currentShipmentIndex={currentShipmentIndex}
                                showPreviousShipment={showPreviousShipment}
                                subtitle={translate('checkout.splitdelivery.subtitle', {
                                    productsInCurrentShipment: productCount.items,
                                    totalProducts: productCount.total,
                                    minMaxDelivery:
                                        getDays(
                                            carrier && deliveryMethod && carrier[deliveryMethod],
                                        ) ??
                                        getFirstAvailableDeliveryDays() ??
                                        '',
                                })}
                            />
                        )}

                        {isTruckDelivery && numberOfShipments === 1 && (
                            <>
                                <DeliverySummary
                                    title={translate('checkout.delivery.title')}
                                    subtitle={translate('checkout.splitdelivery.truckDelivery')}
                                    showSummaryList={true}
                                    products={getProductsByShipment(shipmentId)}
                                />
                                <StyledDeliveryAddressSummary>
                                    <DeliveryAddressSummary />
                                </StyledDeliveryAddressSummary>
                            </>
                        )}

                        {isTruckDelivery && numberOfShipments > 1 && (
                            <div>
                                <SplitDeliverySummary
                                    products={getProductsByShipment(shipmentId)}
                                    title={translate('checkout.splitdelivery.title', {
                                        current: String(currentShipmentIndex),
                                        total: String(numberOfShipments),
                                    })}
                                    info={translate(`checkout.splitdelivery.truckDelivery`)}
                                    subtitle={translate('checkout.splitdelivery.subtitle', {
                                        productsInCurrentShipment: productCount.items,
                                        totalProducts: productCount.total,
                                        minMaxDelivery:
                                            getDays(carrier && carrier['truckDelivery']) ??
                                            getFirstAvailableDeliveryDays() ??
                                            '',
                                    })}
                                    shipmentType={shipmentType}
                                    onSelectDeliveryDate={(value) => setDeliveryDate(value)}
                                    currentShipmentIndex={currentShipmentIndex}
                                    showPreviousShipment={showPreviousShipment}
                                />
                                <StyledDeliveryAddressSummary>
                                    <DeliveryAddressSummary />
                                </StyledDeliveryAddressSummary>
                            </div>
                        )}

                        {showShippingMethods && <ShippingMethods />}

                        {vendorSelectedDroppoint && (
                            <div>
                                <StyledDeliveryAddressSummary>
                                    <DeliveryAddressSummary />
                                </StyledDeliveryAddressSummary>
                            </div>
                        )}
                    </StyledOptions>

                    <StyledCheckoutMobileSubmit
                        aria-label={getCtaLabel(currentShipmentStep, numberOfShipments)}
                        onClick={handleStep}
                        size="large"
                        disabled={!isValid}
                    >
                        {getCtaLabel(currentShipmentStep, numberOfShipments)}
                    </StyledCheckoutMobileSubmit>
                </StyledCheckoutMain>

                <StyledCheckoutAside>
                    <CheckoutSummary
                        ctaLabel={getCtaLabel(currentShipmentStep, numberOfShipments)}
                        onSubmit={handleStep}
                        isCtaDisabled={!isValid}
                    />
                </StyledCheckoutAside>

                {customer?.isStorePersonnel && (
                    <DeliveryEstimateDialog
                        isOpen={isDeliveryDialogOpen}
                        onOpenChange={setIsDeliveryDialogOpen}
                        onConfirm={handleDeliveryDialogConfirm}
                        onCancel={handleDeliveryDialogCancel}
                        deliveryEstimate={longestDeliveryEstimate}
                    />
                )}
            </StyledCheckoutWrapper>
        );
    },
);
